import { Box, Stack, Text, SegmentedControl, Switch, Chip, Group } from '@mantine/core';
import { CookingStyle, effectiveCookingStyle } from '../../types/familyProfile';

// Standing cooking-style traits for the additional_meals experiment. These
// set the meal planning wizard's defaults (batch dinners, lunch mode,
// make-ahead rotations, preferred prep days); any single week can still be
// adjusted in the wizard. Everything here is optional with product defaults,
// so an untouched section means "use the defaults".

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

type CookingStylePreferenceProps = {
  value: { cookingStyle?: CookingStyle };
  onChange: (value: { cookingStyle?: CookingStyle }) => void;
};

// Sections run chronologically (breakfast, lunch, dinner), matching the
// plan view's tabs and the wizard's summary.
export const CookingStylePreference = ({ value, onChange }: CookingStylePreferenceProps) => {
  // Render effective values but write back sparse: only fields the user has
  // touched are stored, so untouched controls track future default changes.
  const style = effectiveCookingStyle(value.cookingStyle);
  const update = (updates: Partial<CookingStyle>) =>
    onChange({ cookingStyle: { ...value.cookingStyle, ...updates } });

  return (
    <Stack gap="xl">
      <Text size="sm" c="dimmed">
        These set your meal planning defaults for weeks that include each
        meal. You can adjust any single week while creating a plan.
      </Text>

      <Stack gap="md">
        <Text fw={500}>Breakfasts</Text>
        <Box>
          <Text size="sm" fw={500} mb={6}>
            Different breakfasts in a full week
          </Text>
          <SegmentedControl
            value={String(style.breakfastRotationSize)}
            onChange={(v) => update({ breakfastRotationSize: Number(v) })}
            data={['1', '2', '3']}
            color="orange"
            fullWidth
          />
          <Text size="xs" c="dimmed" mt={6}>
            How many recipes rotate through a week of breakfasts. Shorter plans
            scale down.
          </Text>
        </Box>
        <Switch
          checked={style.breakfastMakeAhead}
          onChange={(e) => update({ breakfastMakeAhead: e.currentTarget.checked })}
          color="orange"
          size="md"
          label="Batch cook breakfasts"
          description="Cook each breakfast in the rotation once and grab it on repeat days"
        />
      </Stack>

      <Stack gap="md">
        <Text fw={500}>Lunches</Text>
        <SegmentedControl
          value={style.lunchMode}
          onChange={(mode) => update({ lunchMode: mode as 'leftovers' | 'rotation' })}
          data={[
            { label: 'Dinner leftovers', value: 'leftovers' },
            { label: 'Fresh lunches', value: 'rotation' },
          ]}
          color="orange"
          fullWidth
        />
        {style.lunchMode === 'rotation' && (
          <>
            <Box>
              <Text size="sm" fw={500} mb={6}>
                Different lunches in a full week
              </Text>
              <SegmentedControl
                value={String(style.lunchRotationSize)}
                onChange={(v) => update({ lunchRotationSize: Number(v) })}
                data={['1', '2', '3']}
                color="orange"
                fullWidth
              />
              <Text size="xs" c="dimmed" mt={6}>
                How many recipes rotate through a week of lunches. Shorter
                plans scale down.
              </Text>
            </Box>
            <Switch
              checked={style.lunchMakeAhead}
              onChange={(e) => update({ lunchMakeAhead: e.currentTarget.checked })}
              color="orange"
              size="md"
              label="Batch cook lunches"
              description="Cook each lunch in the rotation once and grab it on repeat days"
            />
          </>
        )}
      </Stack>

      <Stack gap="md">
        <Text fw={500}>Dinners</Text>
        <Switch
          checked={style.batchCooking}
          onChange={(e) => update({ batchCooking: e.currentTarget.checked })}
          color="orange"
          size="md"
          label="Batch cook dinners"
          description="Fewer, bigger cooks that cover several nights. Off means a fresh dinner every night."
        />
      </Stack>

      <Stack gap="md">
        <Text fw={500}>Preferred cooking days</Text>
        <Chip.Group
          multiple
          value={style.prepWeekdays}
          onChange={(days) =>
            update({ prepWeekdays: days.length > 0 ? days : undefined })
          }
        >
          <Group gap={6}>
            {WEEKDAYS.map((day) => (
              <Chip key={day} value={day} size="sm">
                {day.slice(0, 3)}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
        <Text size="sm" c="dimmed">
          We'll schedule your batch cooking on these days whenever it keeps
          food fresh.
        </Text>
      </Stack>
    </Stack>
  );
};
